import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'craven_access_token';

// Configure axios defaults
axios.defaults.withCredentials = true;

// Axios interceptor: attach Bearer token fallback on every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to format API error messages
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function storeToken(data) {
  if (data?.access_token) {
    localStorage.setItem(TOKEN_KEY, data.access_token);
  }
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password }
      );
      storeToken(data);
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message
      };
    }
  };

  const register = async (name, email, password, role = 'parent') => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password, role }
      );
      storeToken(data);
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch { /* ignore */ }
    finally {
      clearToken();
      setUser(null);
    }
    return { success: true };
  };

  const isAdmin = user?.role === 'admin';
  const isParent = user?.role === 'parent';

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      checkAuth,
      isAdmin,
      isParent,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// API helper for authenticated requests — axios interceptor auto-attaches Bearer token
export const api = {
  get: (url) => axios.get(`${API_URL}${url}`),
  post: (url, data) => axios.post(`${API_URL}${url}`, data),
  put: (url, data) => axios.put(`${API_URL}${url}`, data),
  patch: (url, data) => axios.patch(`${API_URL}${url}`, data),
  delete: (url) => axios.delete(`${API_URL}${url}`),
};
